import { ContextUrlInput } from "@/components/ContextUrlInput";
import { EnvironmentTypeSelect } from "@/components/EnvironmentTypeSelect";
import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/modal/Modal";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useContextURL } from "@/hooks/use-context-url";
import { useEnvironmentTypes, type EnvironmentTypeEntry } from "@/hooks/use-environment-types";
import { TrackLocations } from "@/hooks/use-segment";
import { Agents, useCreateAgentExecution } from "@/queries/agent-queries";
import { getAgentExecutionUrl } from "@/routes/ai/ai-urls";
import { formatError } from "@/utils/errors";
import { AgentCodeContext_ContextURL } from "gitpod-next-api/gitpod/v1/agent_pb";
import { AgentCodeContext } from "gitpod-next-api/gitpod/v1/agent_pb";
import { useCallback, useId, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";

export const SetUpDevelopmentEnvironmentModal: FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
    const { contextURL, setContextURL, contextURLValid } = useContextURL("");
    const [selectedEnvironment, setSelectedEnvironment] = useState<EnvironmentTypeEntry>();

    const navigate = useNavigate();
    const { toast } = useToast();

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                setContextURL("");
                onClose();
            }
        },
        [onClose, setContextURL],
    );

    const { envTypes, isLoading: isLoadingEnvTypes } = useEnvironmentTypes();
    const createAgentExecution = useCreateAgentExecution();

    const formId = useId();
    const onSubmit = useCallback(
        async (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            try {
                if (!contextURL) {
                    return;
                }
                if (!selectedEnvironment) {
                    return;
                }
                const { agentExecutionId } = await createAgentExecution.mutateAsync({
                    agentId: Agents.DevelopmentEnvironmentSetup.id,
                    codeContext: new AgentCodeContext({
                        context: {
                            case: "contextUrl",
                            value: new AgentCodeContext_ContextURL({
                                url: contextURL,
                                environmentClassId: selectedEnvironment?.clazz.id,
                            }),
                        },
                    }),
                });
                navigate(getAgentExecutionUrl(agentExecutionId));
            } catch (e) {
                toast({
                    title: "Failed to start agent execution",
                    description: formatError(e),
                });
            }
        },
        [contextURL, createAgentExecution, navigate, selectedEnvironment, toast],
    );

    const isValid = contextURLValid && selectedEnvironment;

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                className="max-w-[600px]"
                data-testid="ona-create-project-modal-content"
                data-track-location={TrackLocations.OnaCreateProjectModal}
            >
                <DialogHeader>
                    <DialogTitle>Set up development environment</DialogTitle>
                    <DialogDescription>
                        Ona will help you with the dev container and automations setup
                    </DialogDescription>
                </DialogHeader>

                <DialogBody className="overflow-x flex max-w-full flex-col gap-4">
                    <form
                        id={formId}
                        name="ona-create-project-form"
                        onSubmit={onSubmit}
                        className="flex flex-col gap-2"
                    >
                        <ContextUrlInput
                            label="Repository URL"
                            name="contextURL"
                            value={contextURL}
                            onChange={setContextURL}
                            errorMessage={
                                !!contextURL && !contextURLValid ? "The URL must point to a valid context URL." : ""
                            }
                            autoFocus
                        />

                        <EnvironmentTypeSelect
                            label="Environment class"
                            value={selectedEnvironment}
                            onChange={(v) => setSelectedEnvironment(v)}
                            name="environment-type"
                            loading={isLoadingEnvTypes}
                            disabled={!contextURLValid}
                            envTypes={envTypes}
                        />
                    </form>
                </DialogBody>

                <DialogFooter>
                    <Button
                        type="submit"
                        variant="primary"
                        loading={createAgentExecution.isPending}
                        disabled={!isValid}
                        form={formId}
                        data-testid="ona-create-project-submit-button"
                    >
                        Create
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
